import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OrderItemWithDetailDto } from '../../models/dto/orderItemWithDetailDto';
import { OrderEntity } from '../../models/entity/order/order.entity';
import { UnitEnum } from '../../enum/unit.enum';

export interface CustomerScreenSliderImage {
  title?: string;
  description?: string;
  imageUrl?: string;
  showOnlyImage?: boolean;
  screenOrderNo?: number;
}

export interface CustomerScreenMeta {
  storeName?: string;
  screenName?: string;
  sliderImages?: CustomerScreenSliderImage[];
}

export interface CustomerScreenOrderItem {
  productName?: string;
  quantity?: number;
  numberOfUnits?: number;
  unitPrice?: number;
  finalAmount?: number;
  description?: string;
  totalSquareMeters?: number;
  processTypes?: string[];
}

export interface CustomerScreenOrder {
  totalPrice?: number;
  totalDiscountPrice?: number;
  totalNetPrice?: number;
  serviceFee?: number;
  hangerPrice?: number;
  storageCost?: number;
  bankCommissionAmount?: number;
  finalTotalPrice?: number;
  serviceFeeLabel?: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerScreenService {
  private readonly storageKeyMeta = 'customer-screen-meta';
  private readonly storageKeyOrderItems = 'customer-screen-order-items';
  private readonly storageKeyOrder = 'customer-screen-order';
  private readonly storageKeyTimestamp = 'customer-screen-timestamp';

  private readonly orderItemsSubject = new BehaviorSubject<CustomerScreenOrderItem[]>([]);
  readonly orderItems$: Observable<CustomerScreenOrderItem[]> = this.orderItemsSubject.asObservable();

  private readonly metaSubject = new BehaviorSubject<CustomerScreenMeta>({});
  readonly meta$: Observable<CustomerScreenMeta> = this.metaSubject.asObservable();

  private readonly orderSubject = new BehaviorSubject<CustomerScreenOrder | null>(null);
  readonly order$: Observable<CustomerScreenOrder | null> = this.orderSubject.asObservable();

  constructor() {
    this.loadAllFromStorage();
  }

  private loadAllFromStorage(): void {
    try {
      const meta = localStorage.getItem(this.storageKeyMeta);
      if (meta) {
        const parsedMeta: CustomerScreenMeta = JSON.parse(meta);
        if (parsedMeta && (parsedMeta.storeName || (parsedMeta.sliderImages && parsedMeta.sliderImages.length > 0))) {
          this.metaSubject.next(parsedMeta);
        }
      }

      const orderItems = localStorage.getItem(this.storageKeyOrderItems);
      if (orderItems) {
        const parsedItems: CustomerScreenOrderItem[] = JSON.parse(orderItems);
        this.orderItemsSubject.next(parsedItems);
      }

      const order = localStorage.getItem(this.storageKeyOrder);
      if (order) {
        const parsedOrder: CustomerScreenOrder | null = JSON.parse(order);
        this.orderSubject.next(parsedOrder);
      }
    } catch (e) {
      console.warn('CustomerScreenService: Failed to load from localStorage', e);
    }
  }

  loadFromStorage(): void {
    this.loadAllFromStorage();
  }

  private saveToStorage(): void {
    try {
      const meta = this.metaSubject.value;
      if (meta && (meta.storeName || (meta.sliderImages && meta.sliderImages.length > 0))) {
        localStorage.setItem(this.storageKeyMeta, JSON.stringify(meta));
      }

      const orderItems = this.orderItemsSubject.value;
      localStorage.setItem(this.storageKeyOrderItems, JSON.stringify(orderItems));

      const order = this.orderSubject.value;
      localStorage.setItem(this.storageKeyOrder, JSON.stringify(order));

      localStorage.setItem(this.storageKeyTimestamp, Date.now().toString());
    } catch (e) {
      console.warn('CustomerScreenService: Failed to save to localStorage', e);
    }
  }


  setMeta(meta: CustomerScreenMeta | null | undefined): void {
    const payload = meta ?? {};
    this.metaSubject.next(payload);
    this.saveToStorage();
  }

  private toCustomerScreenOrderItems(items: OrderItemWithDetailDto[] | null | undefined): CustomerScreenOrderItem[] {
    const payload = items ?? [];
    return payload.map(x => {
      const quantity = x?.orderItem?.quantity ?? 0;
      const numberOfUnits = x?.orderItem?.numberOfUnits ?? 1;
      const unitId = x?.orderItem?.unitId;

      const totalSquareMeters = unitId === UnitEnum.SQUARE_METERS
        ? parseFloat((quantity * numberOfUnits).toFixed(2))
        : undefined;

      const processTypes = (x?.orderItemDetails ?? [])
        .map(detail => detail?.orderItemTypeName)
        .filter((name): name is string => !!name && name.trim().length > 0)
        .filter((name, index, self) => self.indexOf(name) === index);

      return {
        productName: x?.productName,
        quantity: quantity,
        numberOfUnits: numberOfUnits,
        unitPrice: (((x?.finalAmount ?? 0) / Math.max(1, quantity * numberOfUnits)) || 0),
        finalAmount: x?.finalAmount ?? 0,
        description: x?.orderItem?.description,
        totalSquareMeters: totalSquareMeters,
        processTypes: processTypes.length > 0 ? processTypes : undefined,
      };
    });
  }

  setOrderItems(items: OrderItemWithDetailDto[] | null | undefined): void {
    const payload = this.toCustomerScreenOrderItems(items);
    this.orderItemsSubject.next(payload);
    this.saveToStorage();
  }

  syncOrderAndItems(order: OrderEntity | null | undefined, items: OrderItemWithDetailDto[] | null | undefined, translate?: any): void {
    const orderPayload = this.toCustomerScreenOrder(order, translate);
    const itemsPayload = this.toCustomerScreenOrderItems(items);

    this.orderSubject.next(orderPayload);
    this.orderItemsSubject.next(itemsPayload);
    this.saveToStorage();
  }

  private toCustomerScreenOrder(order: OrderEntity | null | undefined, translate?: any): CustomerScreenOrder | null {
    if (!order) return null;

    let serviceFeeLabel = '';
    const serviceFee = order.serviceFee ?? 0;
    const hangerPrice = order.hangerPrice ?? 0;
    const storageCost = order.storageCost ?? 0;
    const bankCommissionAmount = order.bankCommissionAmount ?? 0;

    const totalAdditionalFee = serviceFee + hangerPrice + storageCost + bankCommissionAmount;

    if (serviceFee > 0) {
      serviceFeeLabel = translate?.instant('order.serviceFee') ?? 'Servis Ücreti';
    } else if (hangerPrice > 0) {
      serviceFeeLabel = translate?.instant('order.hangerPrice') ?? 'Askı Ücreti';
    } else if (storageCost > 0) {
      serviceFeeLabel = translate?.instant('order.storageCost') ?? 'Depolama Ücreti';
    } else if (bankCommissionAmount > 0) {
      serviceFeeLabel = translate?.instant('order.bankCommissionAmount') ?? 'Banka Komisyonu';
    }

    return {
      totalPrice: order.totalPrice,
      totalDiscountPrice: order.totalDiscountPrice,
      totalNetPrice: order.totalNetPrice,
      serviceFee: totalAdditionalFee > 0 ? totalAdditionalFee : undefined,
      hangerPrice: hangerPrice > 0 ? hangerPrice : undefined,
      storageCost: storageCost > 0 ? storageCost : undefined,
      bankCommissionAmount: bankCommissionAmount > 0 ? bankCommissionAmount : undefined,
      finalTotalPrice: order.finalTotalPrice,
      serviceFeeLabel: serviceFeeLabel || undefined
    };
  }

  setOrder(order: OrderEntity | null | undefined, translate?: any): void {
    const payload = this.toCustomerScreenOrder(order, translate);
    this.orderSubject.next(payload);
    this.saveToStorage();
  }

  reset(): void {
    this.orderItemsSubject.next([]);
    this.orderSubject.next(null);
    this.saveToStorage();
  }
}

